// ============================================================
// services/LayoutService.js — v1.2
// EV Powertrain Simulator — 대시보드 레이아웃 CSV 저장/불러오기
//
// ── 변경 이력 ─────────────────────────────────────────────────
//  v1.2  차량 제원·제어 설정 분리 → VehiclePresetService.js 로 이관
//        레이아웃 CSV 카테고리 구조화 (가독성 개선)
//        v1.1 파일 하위 호환 유지
// ============================================================

export const LAYOUT_VERSION = '1.3';
export const FS_API_SUPPORTED = typeof window !== 'undefined' && 'showDirectoryPicker' in window;

export const DRIVE_KEYS = [
  'rlMassKg', 'rlGrade', 'rlThrottle', 'launchThrottle',
  'reaccelThrottle', 'rlSocScenario', 'rlHeadwind',
  'reaccelV0', 'reaccelV1',
];

// ════════════════════════════════════════════════════════════
//  IndexedDB — 폴더 핸들 영속화
// ════════════════════════════════════════════════════════════
const IDB_NAME  = 'ev-sim-layout';
const IDB_STORE = 'folder-handles';

function openIDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no IDB')); return; }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = (e) => e.target.result.createObjectStore(IDB_STORE);
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}
async function idbGet(key) {
  try {
    const db = await openIDB();
    return new Promise((res, rej) => {
      const req = db.transaction(IDB_STORE,'readonly').objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result ?? null);
      req.onerror   = () => rej(req.error);
    });
  } catch { return null; }
}
async function idbSet(key, val) {
  try {
    const db = await openIDB();
    return new Promise((res, rej) => {
      const req = db.transaction(IDB_STORE,'readwrite').objectStore(IDB_STORE).put(val, key);
      req.onsuccess = () => res(true);
      req.onerror   = () => rej(req.error);
    });
  } catch { return false; }
}
async function idbDel(key) {
  try {
    const db = await openIDB();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE,'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => res(true);
      tx.onerror    = () => res(false);
    });
  } catch { return false; }
}

// ════════════════════════════════════════════════════════════
//  폴더 핸들 모듈 상태
// ════════════════════════════════════════════════════════════
let _saveFolder = { handle: null, name: '' };
let _loadFolder = { handle: null, name: '' };

/**
 * IndexedDB에 저장된 폴더 핸들을 복원하여 모듈 상태(_saveFolder, _loadFolder)에 설정한다.
 *
 * 브라우저 재시작 후 File System Access API의 핸들은 사라지지만
 * IndexedDB에 직렬화된 핸들은 유지되므로, 앱 초기화 시 이 함수를 호출하면
 * 사용자가 폴더를 다시 선택하지 않아도 이전 저장/불러오기 경로를 복원할 수 있다.
 * File System Access API 미지원 환경에서는 즉시 반환(no-op)한다.
 *
 * @returns {Promise<void>}
 */
export async function loadFolderHandles() {
  if (!FS_API_SUPPORTED) return;
  try {
    const sh = await idbGet('saveFolder');
    const lh = await idbGet('loadFolder');
    if (sh) _saveFolder = { handle: sh, name: sh.name };
    if (lh) _loadFolder = { handle: lh, name: lh.name };
  } catch { /* ignore */ }
}

/**
 * 현재 설정된 저장 폴더 정보를 반환한다.
 * @returns {{ handle: FileSystemDirectoryHandle|null, name: string }}
 */
export function getSaveFolder() { return { ..._saveFolder }; }

/**
 * 현재 설정된 불러오기 폴더 정보를 반환한다.
 * @returns {{ handle: FileSystemDirectoryHandle|null, name: string }}
 */
export function getLoadFolder() { return { ..._loadFolder }; }

/**
 * 사용자가 레이아웃 CSV를 저장할 폴더를 선택하고 핸들을 IndexedDB에 영속화한다.
 *
 * showDirectoryPicker로 readwrite 권한 폴더를 선택하면 모듈 상태 _saveFolder를
 * 갱신하고 IndexedDB('saveFolder' 키)에 저장한다.
 * 이후 exportLayout 호출 시 사용자 개입 없이 동일 폴더에 자동 저장된다.
 *
 * @returns {Promise<{ name: string }>}  선택된 폴더 이름
 * @throws {Error}  File System Access API 미지원 환경이면 오류 발생
 */
export async function pickSaveFolder() {
  if (!FS_API_SUPPORTED) throw new Error('File System Access API 미지원 브라우저');
  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  _saveFolder = { handle, name: handle.name };
  await idbSet('saveFolder', handle);
  return { name: handle.name };
}

/**
 * 사용자가 레이아웃 CSV를 불러올 폴더를 선택하고 핸들을 IndexedDB에 영속화한다.
 *
 * importLayout 호출 시 파일 선택 다이얼로그의 시작 경로를 이 폴더로 지정하므로
 * 반복 불러오기 시 탐색 편의성을 높인다.
 *
 * @returns {Promise<{ name: string }>}  선택된 폴더 이름
 * @throws {Error}  File System Access API 미지원 환경이면 오류 발생
 */
export async function pickLoadFolder() {
  if (!FS_API_SUPPORTED) throw new Error('File System Access API 미지원 브라우저');
  const handle = await window.showDirectoryPicker({ mode: 'read' });
  _loadFolder = { handle, name: handle.name };
  await idbSet('loadFolder', handle);
  return { name: handle.name };
}

/**
 * 저장 폴더 핸들을 초기화하고 IndexedDB에서 삭제한다.
 * 이후 exportLayout은 브라우저 다운로드 fallback으로 동작한다.
 * @returns {Promise<void>}
 */
export async function clearSaveFolder() {
  _saveFolder = { handle: null, name: '' };
  await idbDel('saveFolder');
}

/**
 * 불러오기 폴더 핸들을 초기화하고 IndexedDB에서 삭제한다.
 * 이후 importLayout은 기본 파일 선택 다이얼로그에서 시작한다.
 * @returns {Promise<void>}
 */
export async function clearLoadFolder() {
  _loadFolder = { handle: null, name: '' };
  await idbDel('loadFolder');
}

// ════════════════════════════════════════════════════════════
//  CSV 직렬화 / 역직렬화
// ════════════════════════════════════════════════════════════
function axisEntryToRows(axId, entry) {
  const rows = [];
  ['axX','axY','axY2','axY3'].forEach(axisKey => {
    const ax = entry[axisKey];
    if (!ax) return;
    rows.push([`ax_${axId}_${axisKey}_auto`, ax.auto]);
    rows.push([`ax_${axId}_${axisKey}_min`,  ax.min]);
    rows.push([`ax_${axId}_${axisKey}_max`,  ax.max]);
  });
  ['hideY','hideY2','hideY3'].forEach(flag => {
    rows.push([`ax_${axId}_${flag}`, entry[flag] ?? false]);
  });
  return rows;
}

// ## 로 시작하는 줄은 주석 (parseCSV에서 자동 스킵)
const C = (text) => `## ${text}`;
const SEP = () => `## ${'─'.repeat(56)}`;

function buildCSVLines(uiStore, axesStore, paramsStore) {
  const ui = uiStore.getState(), axes = axesStore.getState(), params = paramsStore.getState();
  const lines = [];
  const kv = (k, v) => `${k},${v}`;

  // ── 헤더 ──────────────────────────────────────────────────
  // v1.3: Phase 2/3 9-tab structure — TAB_ID_MIGRATION guard added
  lines.push(SEP());
  lines.push(C(' EV Powertrain Simulator — 레이아웃 설정 / Layout Settings'));
  lines.push(C(`  version: ${LAYOUT_VERSION}   saved: ${new Date().toISOString()}`));
  lines.push(C('  ※ 차량 제원·제어 설정은 별도 차량 CSV로 저장/불러오기 하세요.'));
  lines.push(SEP());
  lines.push('');

  // ── [1] 메타 ──────────────────────────────────────────────
  lines.push(C(' [1] 메타 / Meta'));
  lines.push(kv('ev_sim_layout_version', LAYOUT_VERSION));
  lines.push(kv('saved_at', new Date().toISOString()));
  lines.push('');

  // ── [2] UI 설정 ────────────────────────────────────────────
  lines.push(C(' [2] UI 설정 / UI Settings  — 언어 · 테마 · 활성 탭'));
  lines.push(kv('lang',      ui.lang      ?? 'ko'));
  lines.push(kv('isDark',    ui.isDark    ?? false));
  lines.push(kv('activeTab', ui.activeTab ?? 'roadload'));
  lines.push(kv('activeCat', ui.activeCat ?? 'vd'));
  lines.push('');

  // ── [3] 드라이브 파라미터 ─────────────────────────────────
  lines.push(C(' [3] 드라이브 파라미터 / Drive Parameters'));
  lines.push(C('     rlMassKg: 추가 질량(kg) | rlGrade: 경사(deg) | rlThrottle: 스로틀(%)'));
  lines.push(C('     launchThrottle: 발진 스로틀 | reaccelThrottle: 재가속 스로틀'));
  lines.push(C('     rlSocScenario: SOC 시나리오 | rlHeadwind: 맞바람(m/s)'));
  lines.push(C('     reaccelV0: 재가속 시작속도(km/h) | reaccelV1: 재가속 목표속도(km/h)'));
  DRIVE_KEYS.forEach(k => {
    const v = params[k];
    if (v !== undefined) lines.push(kv(`drv_${k}`, v));
  });
  lines.push('');

  // ── [4] 축 설정 ────────────────────────────────────────────
  lines.push(C(' [4] 축 설정 / Chart Axes  — 각 그래프 X/Y 축 범위 및 숨김'));
  lines.push(C('     형식: ax_{탭ID}_{축}_{auto|min|max}'));
  Object.entries(axes.axes).forEach(([id, entry]) =>
    axisEntryToRows(id, entry).forEach(([k, v]) => lines.push(kv(k, v)))
  );

  return lines;
}


// ── Phase 2/3 탭 ID 마이그레이션 맵 ──────────────────────
// 구형 레이아웃 CSV가 저장한 activeTab 값 → 현재 유효 탭 ID 로 리매핑
// 이유: 6탭 → 9탭 구조 전환 시 일부 내부 section ID가 top-level tab으로 승격됨
const TAB_ID_MIGRATION = {
  // Phase 3: fmea_safety의 TSR 섹션 → 독립 탭으로 분리됨
  // (tsr는 과거 activeTab 값으로 저장된 적 없으나 방어적 등록)
  'tsr':                   'technical_safety_concept',
  // Phase 2: fault_injection/asil_recheck/asil_decomp → safety_verification
  'fault_injection':       'safety_verification',
  'asil_recheck':          'safety_verification',
  'asil_decomp':           'safety_verification',
  // 구형 단일 hara 섹션명 → hara (동일, 방어적 등록)
  'hazard_list':           'hara',
  'asil_matrix':           'hara',
  'safety_goal':           'hara',
};

function migrateTabId(id) {
  return TAB_ID_MIGRATION[id] ?? id;
}

function parseCSV(text) {
  const map = new Map();
  text.split('\n').forEach(line => {
    const t = line.trim();
    if (!t || t.startsWith('#')) return; // 주석·빈 줄 스킵
    const i = t.indexOf(','); if (i === -1) return;
    const k = t.slice(0,i).trim(); const v = t.slice(i+1).trim();
    if (k) map.set(k, v);
  });
  return map;
}

function makeFilename() {
  const d = new Date(), p = n => String(n).padStart(2,'0');
  return `evsim_layout_${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}`
       + `_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.csv`;
}

function triggerDownload(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

async function requestPermission(handle, mode) {
  if (!handle) return false;
  const p = await handle.queryPermission({ mode });
  if (p === 'granted') return true;
  return (await handle.requestPermission({ mode })) === 'granted';
}

async function writeToFolder(dirHandle, filename, content) {
  const ok = await requestPermission(dirHandle, 'readwrite');
  if (!ok) throw new Error('폴더 쓰기 권한이 거부되었습니다.');
  const fh = await dirHandle.getFileHandle(filename, { create: true });
  const wr = await fh.createWritable();
  await wr.write(content); await wr.close();
}

// ════════════════════════════════════════════════════════════
//  exportLayout
// ════════════════════════════════════════════════════════════
/**
 * 현재 UI 설정·차트 축·드라이브 파라미터를 레이아웃 CSV로 내보낸다.
 *
 * CSV 직렬화 방식: buildCSVLines가 Zustand 스토어 상태를 읽어
 * [1]메타 → [2]UI설정 → [3]드라이브파라미터 → [4]축설정 순으로
 * `키,값` 형태의 행을 생성하며 `## ` 주석으로 섹션을 구분한다.
 * pickSaveFolder로 폴더가 지정된 경우 File System Access API로 직접 기록하고,
 * 미지정이거나 실패 시 브라우저 다운로드(fallback)로 처리한다.
 *
 * @param {object} uiStore      Zustand UI 스토어 (lang, isDark, activeTab, activeCat)
 * @param {object} axesStore    Zustand 축 스토어 (axes 맵)
 * @param {object} paramsStore  Zustand 파라미터 스토어 (DRIVE_KEYS 관련 setter 포함)
 * @returns {Promise<{ filename: string, folder: string }>}
 *   저장된 파일명과 폴더 이름 (브라우저 다운로드 시 folder = '')
 */
export async function exportLayout(uiStore, axesStore, paramsStore) {
  const csv = buildCSVLines(uiStore, axesStore, paramsStore).join('\n');
  const fn  = makeFilename();

  if (FS_API_SUPPORTED && _saveFolder.handle) {
    try {
      await writeToFolder(_saveFolder.handle, fn, csv);
      return { filename: fn, folder: _saveFolder.name };
    } catch (err) {
      console.warn('[LayoutService] 폴더 저장 실패, fallback:', err.message);
      triggerDownload(csv, fn);
      return { filename: fn, folder: '' };
    }
  }
  triggerDownload(csv, fn);
  return { filename: fn, folder: '' };
}

// ════════════════════════════════════════════════════════════
//  importLayout
// ════════════════════════════════════════════════════════════
/**
 * 레이아웃 CSV 파일을 파싱하여 UI 설정·축·드라이브 파라미터를 복원한다.
 *
 * file이 null이면 showOpenFilePicker로 파일 선택 다이얼로그를 열고,
 * _loadFolder가 지정된 경우 해당 폴더를 시작 위치로 설정한다.
 * 파싱은 parseCSV → Map<key, value> 변환 후 각 스토어 setter를 호출한다.
 *
 * 버전 호환성:
 *   - v1.1: vehA_{n}/vehB_{n} 키를 paramsStore.setParamsA/B로 복원 (하위 호환)
 *   - v1.2: 차량 설정이 VehiclePresetService로 분리되어 레이아웃 CSV에 미포함
 *   - v1.3(현재): 9탭 구조 + TAB_ID_MIGRATION으로 구형 activeTab 값 마이그레이션
 *
 * @param {File|null}  file          직접 전달할 File 객체 (null이면 파일 선택 다이얼로그 표시)
 * @param {object}     uiStore       Zustand UI 스토어
 * @param {object}     axesStore     Zustand 축 스토어
 * @param {object}     paramsStore   Zustand 파라미터 스토어
 * @param {Function}   onResult      결과 콜백 ({ ok: boolean, message: string, filename?: string }) => void
 * @returns {Promise<void>}
 */
export async function importLayout(file, uiStore, axesStore, paramsStore, onResult) {
  let targetFile = file;

  if (!targetFile && FS_API_SUPPORTED) {
    try {
      const opts = {
        types: [{ description: 'EV Sim Layout CSV', accept: { 'text/csv': ['.csv'] } }],
        multiple: false,
      };
      if (_loadFolder.handle) {
        const ok = await requestPermission(_loadFolder.handle, 'read');
        if (ok) opts.startIn = _loadFolder.handle;
      }
      const [fh] = await window.showOpenFilePicker(opts);
      targetFile  = await fh.getFile();
    } catch (err) {
      if (err.name === 'AbortError') { onResult?.({ ok: false, message: '취소됨' }); return; }
      onResult?.({ ok: false, message: `파일 선택 오류: ${err.message}` }); return;
    }
  }

  if (!targetFile) { onResult?.({ ok: false, message: '파일이 선택되지 않았습니다.' }); return; }

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const map     = parseCSV(e.target.result);
      const version = map.get('ev_sim_layout_version');
      if (!version) { onResult?.({ ok: false, message: '유효한 레이아웃 CSV 파일이 아닙니다.' }); return; }
      if (!['1.1','1.2',LAYOUT_VERSION].includes(version)) {
        console.warn(`[LayoutService] 버전 불일치: ${version}`);
      }

      if (map.has('lang'))      uiStore.setLang(map.get('lang'));
      if (map.has('isDark'))    uiStore.setIsDark(map.get('isDark') === 'true');
      if (map.has('activeTab')) uiStore.setActiveTab(migrateTabId(map.get('activeTab')));
      if (map.has('activeCat')) uiStore.setActiveCat(map.get('activeCat'));

      const axesMap = {};
      map.forEach((val, key) => {
        if (!key.startsWith('ax_')) return;
        const rest = key.slice(3);
        const fm   = rest.match(/^(.+)_(hideY[23]?|hideY)$/);
        if (fm) { if (!axesMap[fm[1]]) axesMap[fm[1]] = {}; axesMap[fm[1]][fm[2]] = (val === 'true'); return; }
        const am   = rest.match(/^(.+)_(axX|axY2|axY3|axY)_(auto|min|max)$/);
        if (am) {
          const [, id, ak, prop] = am;
          if (!axesMap[id]) axesMap[id] = {};
          if (!axesMap[id][ak]) axesMap[id][ak] = {};
          axesMap[id][ak][prop] = prop === 'auto' ? (val === 'true') : parseFloat(val);
        }
      });
      if (Object.keys(axesMap).length) axesStore.bulkSet(axesMap);

      DRIVE_KEYS.forEach(key => {
        const raw = map.get(`drv_${key}`); if (!raw) return;
        const val = key === 'rlSocScenario' ? raw : parseFloat(raw);
        const fn  = 'set' + key[0].toUpperCase() + key.slice(1);
        if (typeof paramsStore[fn] === 'function') paramsStore[fn](val);
      });

      // ── v1.1 하위 호환: 구형 파일에 vehA_/vehB_ 있으면 복원 ──
      if (version === '1.1') {
        const castValue = (v) => {
          if (v === 'true') return true;
          if (v === 'false') return false;
          if (v.startsWith('"') && v.endsWith('"')) return v.slice(1, -1);
          const n = parseFloat(v);
          return isNaN(n) ? v : n;
        };
        const vehA = {}, vehB = {};
        map.forEach((val, key) => {
          if (key.startsWith('vehA_')) vehA[key.slice(5)] = castValue(val);
          if (key.startsWith('vehB_')) vehB[key.slice(5)] = castValue(val);
        });
        if (map.has('vehicleAKey')) paramsStore.setVehicleAKey?.(map.get('vehicleAKey'));
        if (map.has('vehicleBKey')) paramsStore.setVehicleBKey?.(map.get('vehicleBKey'));
        if (Object.keys(vehA).length) paramsStore.setParamsA(vehA);
        if (Object.keys(vehB).length) paramsStore.setParamsB(vehB);
      }

      onResult?.({ ok: true, message: `불러오기 완료 (${targetFile.name})`, filename: targetFile.name });
    } catch (err) {
      onResult?.({ ok: false, message: `파싱 오류: ${err.message}` });
    }
  };
  reader.onerror = () => onResult?.({ ok: false, message: '파일 읽기 실패.' });
  reader.readAsText(targetFile, 'utf-8');
}